import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../authContext.js";
import '../css/Login.css';

const FIELD =
    "w-full rounded-md border px-3 py-2 pr-10 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500";

export default function Login() {
    const { handleSubmit, control } = useForm({
        defaultValues: { email: '', password: '' },
    });
    const navigate = useNavigate();
    const { authMethod, user, login, loginCas, loadingInitial } = useAuth();

    const [showPassword, setShowPassword] = useState(false);
    const [errorLogin, setErrorLogin] = useState(null);

    useEffect(() => {
        if (user) {
            navigate('/');
        } else if (authMethod.toLowerCase() === 'cas') {
            loginCas();
        }
    }, [user, authMethod, loginCas, navigate]);

    const onSubmit = async ({ email, password }) => {
        try {
            const result = await login(email, password);
            if (result && result.success) {
                navigate('/');
            } else {
                setErrorLogin(result.message);
            }
        } catch (err) {
            setErrorLogin(err.message);
        }
    };

    return (
        <div className="flex h-screen w-full items-center justify-center bg-gray-50 p-6">
            <div className="w-full max-w-sm rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <img
                        src="/test_logo_beeplanif.png"
                        alt=""
                        className="mx-auto mb-4 max-h-28 w-3/4 object-contain"
                    />
                    <div className="flex flex-col gap-3">
                        {errorLogin && (
                            <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                                {errorLogin}
                            </p>
                        )}

                        <Controller
                            name="email"
                            control={control}
                            rules={{ required: "Veuillez renseigner ce champ" }}
                            render={({ field, fieldState }) => (
                                <div>
                                    <div className="relative">
                                        <input
                                            {...field}
                                            type="email"
                                            placeholder="Adresse Mail"
                                            aria-label="Adresse mail"
                                            aria-invalid={fieldState.invalid}
                                            className={`${FIELD} ${fieldState.invalid ? 'border-red-500' : 'border-gray-300'}`}
                                        />
                                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                                            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                                                <path d="M10 10a4 4 0 100-8 4 4 0 000 8zm0 2c-4 0-7 2-7 4.5V18h14v-1.5c0-2.5-3-4.5-7-4.5z" />
                                            </svg>
                                        </span>
                                    </div>
                                    {fieldState.error?.message && (
                                        <p className="mt-1 text-xs text-red-600">{fieldState.error.message}</p>
                                    )}
                                </div>
                            )}
                        />

                        <Controller
                            name="password"
                            control={control}
                            rules={{ required: "Veuillez renseigner ce champ" }}
                            render={({ field, fieldState }) => (
                                <div>
                                    <div className="relative">
                                        <input
                                            {...field}
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="Mot de passe"
                                            aria-label="Mot de passe"
                                            aria-invalid={fieldState.invalid}
                                            className={`${FIELD} ${fieldState.invalid ? 'border-red-500' : 'border-gray-300'}`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword((shown) => !shown)}
                                            aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                                            className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                                        >
                                            {showPassword ? (
                                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                                </svg>
                                            ) : (
                                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                    {fieldState.error?.message && (
                                        <p className="mt-1 text-xs text-red-600">{fieldState.error.message}</p>
                                    )}
                                </div>
                            )}
                        />

                        <button
                            type="submit"
                            disabled={loadingInitial}
                            className="mt-1 rounded-md border border-brand-500 px-3 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-60"
                        >
                            {loadingInitial ? 'Connexion…' : 'Se connecter'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
